import { provide, inject, ref, InjectionKey, Ref } from 'vue'

export interface AppLogos {
  wideLogo: string
  miniLogo: string
  svgLogo: string
  darkLogo: string
}

const AppLogoKey: InjectionKey<Ref<AppLogos>> = Symbol('Application logo')

/**
 * Hand an app's logos to everything under it.
 *
 * An app that knows its own branding passes it in — that is the whole of what adok does,
 * from `app/assets`, so a renamed file is a build error rather than an empty `<img>`.
 *
 * Without a second argument the logos are globbed instead, by a key built from the app's
 * name. That path is traforama's and only traforama's; it is kept because switching it
 * over is a change to that app, not to this hook. The `import.meta.glob` pattern has to
 * stay a string literal either way — Vite resolves it at build time and cannot follow a
 * variable.
 */
const provideLogo = (app: string, logos?: AppLogos) => {
  if (logos) {
    provide(AppLogoKey, ref(logos))
    return
  }

  const found = import.meta.glob<string>('@/apps/traforama/assets/logos/*', {
    eager: true,
    import: 'default'
  })
  const dir = '/src/apps/traforama/assets/logos'

  // `?? ''` because a lookup misses whenever a logo is renamed or moved; an empty src
  // draws nothing and says so in the network tab, where `undefined` requests a file.
  provide(
    AppLogoKey,
    ref({
      wideLogo: found[`${dir}/${app}-full.png`] ?? '',
      miniLogo: found[`${dir}/${app}-mini.png`] ?? '',
      svgLogo: found[`${dir}/${app}-svg.svg`] ?? '',
      darkLogo: found[`${dir}/${app}-white.png`] ?? ''
    })
  )
}

const useLogo = (): AppLogos => {
  const logos = inject(AppLogoKey)

  if (!logos) throw new Error('No logos provided!!!')
  return logos.value
}

export { provideLogo, useLogo }
